import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { searchInverters, searchBatteries, getManufacturers } from '../../api/compatibility';
import { ProductImage } from '../ProductImage';

// kind (API) -> productType (zdjęcia/fallbacki). 'inverter'->'inwerter', 'battery'->'magazyn'.
const IMG_TYPE = { inverter: 'inwerter', battery: 'magazyn' };

const SEARCH_FN = { inverter: searchInverters, battery: searchBatteries };
const DEBOUNCE_MS = 300;

// Selektor urządzenia z wyszukiwaniem (debounce), filtrem po producencie i pełną
// obsługą klawiatury (combobox: strzałki / Enter / Escape). Używany dla falowników
// i magazynów — kierunek (`kind`) steruje, które API i pola API wywołać.
const DeviceSearchSelect = ({ kind, label, value, onChange, placeholder }) => {
  const { t } = useTranslation('zgodnosc');
  const [query, setQuery] = useState(value?.product_name || '');
  const [manufacturer, setManufacturer] = useState('');
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [error, setError] = useState(false);

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  // Lista producentów do filtra — z dedykowanego /manufacturers (ANEKS A5), nie z
  // pełnego pobrania katalogu (nie skaluje się przy rosnącej liczbie produktów).
  useEffect(() => {
    let cancelled = false;
    getManufacturers(kind).then((res) => {
      if (cancelled) return;
      setManufacturerOptions((res.items || []).map((it) => it.name));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [kind]);

  useEffect(() => {
    if (value) { setQuery(value.product_name); return; }
  }, [value]);

  const runSearch = (q, m) => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    SEARCH_FN[kind]({ q, manufacturer: m || undefined, limit: 20 })
      .then((res) => {
        if (reqId !== requestIdRef.current) return;
        setResults(res.items || []);
        setLoading(false);
      })
      .catch(() => {
        if (reqId !== requestIdRef.current) return;
        setError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query, manufacturer), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, manufacturer, open, kind]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selectDevice = (device) => {
    onChange(device);
    setQuery(device.product_name);
    setOpen(false);
    setHighlighted(-1);
  };

  const clearSelection = () => {
    onChange(null);
    setQuery('');
    setResults([]);
    // Zostaw otwartą listę i focus w polu — jednym kliknięciem "X" użytkownik od razu
    // widzi pełną listę do wyboru, zamiast musieć kliknąć jeszcze raz w pole.
    setOpen(true);
    setHighlighted(-1);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[highlighted]) selectDevice(results[highlighted]); }
    else if (e.key === 'Escape') { setOpen(false); setHighlighted(-1); }
  };

  const listboxId = `${kind}-search-listbox`;
  const imgType = IMG_TYPE[kind] || 'inwerter';

  return (
    <div ref={rootRef} className="relative">
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>

      {/* Pole wyszukiwania — pełna szerokość kolumny (nazwa produktu bywa długa). */}
      <div className="relative">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={query}
          placeholder={placeholder}
          onFocus={(e) => { setOpen(true); e.target.select(); }}
          onChange={(e) => { setQuery(e.target.value); onChange(null); setOpen(true); setHighlighted(-1); }}
          onKeyDown={onKeyDown}
          className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {query && (
          <button
            type="button"
            onClick={clearSelection}
            aria-label={t('wyczysc_wybor')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <FontAwesomeIcon icon={faXmark} className="text-sm" />
          </button>
        )}

        {open && (
          <ul id={listboxId} role="listbox" className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
            {loading ? (
              <li className="px-4 py-3 text-sm text-gray-400">{t('szukam')}</li>
            ) : error ? (
              <li className="px-4 py-3 text-sm text-rose-500">
                {t('blad_wyszukiwania')}{' '}
                <button type="button" className="underline font-semibold" onClick={() => runSearch(query, manufacturer)}>{t('sprobuj_ponownie')}</button>
              </li>
            ) : results.length === 0 ? (
              <li className="px-4 py-3 text-sm text-gray-400">{t('brak_wynikow', { fraza: query })}</li>
            ) : (
              results.map((item, i) => (
                <li
                  key={item.id}
                  id={`${listboxId}-opt-${i}`}
                  role="option"
                  aria-selected={i === highlighted}
                  onMouseDown={(e) => { e.preventDefault(); selectDevice(item); }}
                  onMouseEnter={() => setHighlighted(i)}
                  className={`px-4 py-2.5 text-sm cursor-pointer ${i === highlighted ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <div className="font-medium truncate">{item.product_name}</div>
                  <div className="text-xs text-gray-400">{item.manufacturer}</div>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      {/* Filtr producenta — dopóki nic nie wybrano (zawęża wyszukiwanie). */}
      {!value && manufacturerOptions.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-gray-400 shrink-0">{t('producent_dwukropek')}</span>
          <div className="relative flex-1">
            <select
              value={manufacturer}
              onChange={(e) => { setManufacturer(e.target.value); setOpen(true); }}
              aria-label="Filtruj po producencie"
              className="appearance-none w-full pl-3 pr-7 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('wszyscy_producenci')}</option>
              {manufacturerOptions.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <FontAwesomeIcon icon={faChevronDown} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
          </div>
        </div>
      )}

      {/* Wybrane urządzenie — zdjęcie w ramce + pełna nazwa (input bywa ucięty). */}
      {value && (
        <div className="mt-2 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-2">
          <div className="w-12 h-12 shrink-0 flex items-center justify-center rounded-lg border border-gray-200 bg-white p-1">
            <ProductImage src={value.img_url} productType={imgType} alt={value.product_name} className="max-w-full max-h-full object-contain" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 leading-snug">{value.product_name}</p>
            {value.manufacturer && <p className="text-xs text-gray-400 mt-0.5">{value.manufacturer}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceSearchSelect;
